import React, { useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Divider,
    Breadcrumbs,
    Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

const PrivacyPolicy = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            id: 1,
            title: "1. Information We Collect",
            content: "We may collect the following types of information:\n\na) Personal Information\n- Name\n- Email address\n- Mobile number\n- Delivery address\n- Payment-related details (processed securely via third-party gateways)\n\nb) Usage Data\n- App usage details\n- Device information (device type, OS version)\n- IP address\n- Log data and crash reports"
        },
        {
            id: 2,
            title: "2. How We Use Your Information",
            content: "We use your information to:\n- Create and manage your account\n- Process orders and payments\n- Deliver products and services\n- Provide customer support\n- Improve app performance and user experience\n- Send order updates, notifications, and service-related communications\n- Detect and prevent fraud or misuse"
        },
        {
            id: 3,
            title: "3. Sharing of Information",
            content: "We do not sell your personal data. We may share your information only with:\n- Delivery partners (for order fulfillment)\n- Payment gateway providers (for secure transactions)\n- Service providers who assist in app operations\n- Legal authorities if required by law\n\nAll third-party partners are required to protect your data."
        },
        {
            id: 4,
            title: "4. Data Security",
            content: "We implement reasonable security measures to protect your personal information. However, no method of transmission over the internet is 100% secure. We strive to use commercially acceptable means to protect your data."
        },
        {
            id: 5,
            title: "5. Cookies & Tracking Technologies",
            content: "Our website/app may use cookies and similar technologies to:\n- Improve user experience\n- Analyze traffic and usage patterns\n- Remember user preferences\n\nYou can control cookie settings through your browser."
        },
        {
            id: 6,
            title: "6. User Rights",
            content: "You have the right to:\n- Access your personal data\n- Request correction or deletion of your data\n- Withdraw consent (where applicable)\n- Request account deletion by contacting support"
        },
        {
            id: 7,
            title: "7. Children’s Privacy",
            content: "Our services are not intended for users under the age of 18. We do not knowingly collect personal information from children."
        },
        {
            id: 8,
            title: "8. Third-Party Links",
            content: "Our app or website may contain links to third-party sites. We are not responsible for the privacy practices of those websites."
        },
        {
            id: 9,
            title: "9. Changes to This Privacy Policy",
            content: "We may update this Privacy Policy from time to time. Any changes will be posted on this page. Continued use of the app means you accept the updated policy."
        }
    ];

    return (
        <Box sx={{ bgcolor: '#fbfbfb', minHeight: '100vh', pb: 8 }}>
            {/* Header Section with Gradient */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
                    color: 'white',
                    py: { xs: 6, md: 10 },
                    mb: 6,
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            mb: 2,
                            letterSpacing: '-0.02em',
                            fontFamily: "'Playfair Display', serif"
                        }}
                    >
                        Privacy Policy
                    </Typography>
                    <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                        aria-label="breadcrumb"
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            display: 'flex',
                            justifyContent: 'center',
                            '& .MuiBreadcrumbs-li': {
                                fontSize: '0.9rem',
                                fontWeight: 500
                            }
                        }}
                    >
                        <Link
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate('/')}
                            sx={{ cursor: 'pointer', '&:hover': { color: 'white' } }}
                        >
                            Home
                        </Link>
                        <Typography color="white">Privacy Policy</Typography>
                    </Breadcrumbs>
                </Container>
            </Box>

            <Container maxWidth="md">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 6 },
                        borderRadius: 4,
                        border: '1px solid #eee',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
                        background: '#ffffff'
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            mb: 4,
                            lineHeight: 1.8,
                            color: '#444',
                            fontSize: '1.1rem'
                        }}
                    >
                        <strong>Glam Company</strong> (“we”, “our”, “us”) operates the Glam Company mobile application and website{' '}
                        <Link href="https://glamcompany.in" target="_blank" sx={{ color: '#000', fontWeight: 600 }}>
                            glamcompany.in
                        </Link>. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our services.
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            mb: 4,
                            lineHeight: 1.8,
                            color: '#444',
                            fontSize: '1.1rem'
                        }}
                    >
                        By using our app or website, you agree to the collection and use of information in accordance with this Privacy Policy.
                    </Typography>

                    <Divider sx={{ mb: 6 }} />

                    {sections.map((section) => (
                        <Box key={section.id} sx={{ mb: 5 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    mb: 2,
                                    color: '#000',
                                    fontSize: '1.25rem',
                                    letterSpacing: '0.01em'
                                }}
                            >
                                {section.title}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    whiteSpace: 'pre-line',
                                    color: '#555',
                                    lineHeight: 1.7,
                                    fontSize: '1rem'
                                }}
                            >
                                {section.content}
                            </Typography>
                        </Box>
                    ))}

                    <Divider sx={{ my: 6 }} />

                    <Box
                        sx={{
                            p: 4,
                            bgcolor: '#f9f9f9',
                            borderRadius: 3,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
                            10. Contact Us
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#555', mb: 1 }}>
                            If you have any questions about this Privacy Policy or your data, please contact us at:
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: '#000',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            📧 tvfmsynergy@gmail.com
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;